import moment from 'moment';
import {
  EAutoRange,
  EllipsePointMarker,
  FastColumnRenderableSeries,
  FastLineRenderableSeries,
  NumberRange,
  NumericLabelProvider,
  NumericTickProvider,
  StackedColumnCollection,
  StackedColumnRenderableSeries,
  XyDataSeries,
  XyScatterRenderableSeries,
} from 'scichart';
import { formatNumber } from 'utils/number';
import type { SciChartInitContext } from 'components/charts/scichart';
import { toBarValues, toLineSeries, toScatterPoints } from './util';
import type { ChartSeriesEntry, ChartSpec } from './types';

/**
 * The shared SciChart drawing code for a `chart_series` result. Both the generic catalog
 * renderer (`SpecChart`) and the Custom builder (`CustomChart`) draw through here, so the
 * two never diverge — a stacked bar stacks the same way, a colorBy fan-out gets the same
 * legend, and a line handles a date OR a category X-axis identically. Net-new (INS-9994):
 * this is `SpecChart`'s render logic promoted to a reusable module.
 */

/**
 * One hue per series (multi-series overlays + stackedBar segments) — the Carbon categorical
 * palette. Left as raw hex on purpose: SciChart's renderable-series `fill` / `stroke` take
 * concrete color STRINGS drawn onto a WebGL surface — they don't resolve CSS vars or SCSS
 * tokens — so these can't be tokenized the way component CSS is. SciChart draws each series
 * with the hue at its index; the legend mirrors it via `LegendItem.colorIndex`. Keep in sync
 * with `$series-colors` in `chart-legend.module.scss` (the swatch classes).
 */
export const SERIES_COLORS = [
  '#4589ff',
  '#42be65',
  '#fa4d56',
  '#8a3ffc',
  '#ff832b',
  '#009d9a',
  '#d12771',
];

/**
 * Compact axis number ("$1.2M", "340K", "1,235").
 *
 * @param {number} value - The raw value.
 * @return {string} The compact label.
 */
export const compact = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${formatNumber(value / 1_000_000, 1, 1, 'decimal', false)}M`;
  }
  if (abs >= 1_000) {
    return `${formatNumber(value / 1_000, 0, 0, 'decimal', false)}K`;
  }
  return formatNumber(value, 0, 0, 'decimal', true) ?? `${Math.round(value)}`;
};

/** One category tick per bar (integer positions `0…count-1`). */
export class IndexTickProvider extends NumericTickProvider {
  count = 0;

  override getMajorTicks(): number[] {
    return Array.from({ length: this.count }, (_, i) => i);
  }

  override getMinorTicks(): number[] {
    return [];
  }
}

/**
 * A category tick's display text — falls back to "(None)" for a null/blank group value
 * (e.g. a slot with no formation), so the bar isn't left with an empty axis label.
 *
 * @param {string | null | undefined} label - The raw group label from the response.
 * @return {string} The tick text.
 */
export const displayLabel = (label: string | null | undefined): string => {
  const text = label ? String(label) : '';
  return text.trim() ? text : '(None)';
};

/**
 * Drops a trailing unit hint from a field label for a compact legend row
 * ("Net Oil (Mbbl)" → "Net Oil", "Net Cash Flow ($)" → "Net Cash Flow").
 *
 * @param {string} label - The field's display label.
 * @return {string} The label without its trailing unit parenthetical.
 */
const stripUnit = (label: string): string =>
  label.replace(/\s*\([^)]*\)\s*$/, '');

/**
 * A colorBy series name, `"<field> · <category>"` (Query::Packages::ChartSeries), split into its
 * two parts at the first separator, since a field key never contains one; null for any other name.
 *
 * @param {string} name - A response series name.
 * @return {object | null} `{ field, category }`, or null.
 */
const splitSeriesName = (
  name: string
): { field: string; category: string } | null => {
  const at = name.indexOf(' · ');
  return at < 0
    ? null
    : { field: name.slice(0, at), category: name.slice(at + 3) };
};

/**
 * Legend text for a series — the colorBy category ("PDP") when a metric is fanned into
 * `"<field> · <category>"`; otherwise the human field label (from the `chart_field_options`
 * vocabulary, keyed by `series.field`, with its unit hint dropped) when supplied, falling
 * back to the raw series name. This is what makes single-metric charts (e.g. Cumulative Cash
 * Flow) legend as "Net Cash Flow" rather than the raw `netCashflow`.
 *
 * @param {ChartSeriesEntry} series - A response series.
 * @param {Map<string, string>} [labelByField] - Field key → display label, from `chart_field_options`.
 * @return {string} The legend label.
 */
export const legendLabel = (
  series: ChartSeriesEntry,
  labelByField?: Map<string, string>
): string => {
  const parts = splitSeriesName(series.name);
  if (parts) {
    return parts.category.trim();
  }
  const label = labelByField?.get(series.field);
  return (label ? stripUnit(label) : series.name).trim();
};

/** The field key of the Asset dimension, whose values are Tract names. */
export const ASSET_FIELD = 'asset';

/**
 * The series with each Asset named as the tables name it: a Tract the package holds in part
 * followed by its "(N of M Subtracts)". The Assets are the X categories when X is Asset, and
 * the series' colorBy categories (`"<field> · <category>"`, which the legend reads) when Color
 * By is.
 *
 * @param {ChartSeriesEntry[]} seriesData - The response series.
 * @param {ChartSpec} spec - The spec they answer.
 * @param {Function} nameForAsset - An Asset as the tables name it (`usePartialTracts().nameForAsset`);
 *   a blank one stays blank, which `displayLabel` reads as "(None)".
 * @return {ChartSeriesEntry[]} The same series, Assets labelled; the input itself when none is Asset.
 */
export function withAssetLabels(
  seriesData: ChartSeriesEntry[],
  spec: ChartSpec,
  nameForAsset: (name: string) => string
): ChartSeriesEntry[] {
  const xIsAsset = spec.x.field === ASSET_FIELD;
  const colorByAsset = spec.colorBy === ASSET_FIELD;
  if (!xIsAsset && !colorByAsset) {
    return seriesData;
  }
  return seriesData.map((series) => {
    const parts = colorByAsset ? splitSeriesName(series.name) : null;
    return {
      ...series,
      name: parts
        ? `${parts.field} · ${nameForAsset(parts.category)}`
        : series.name,
      values:
        xIsAsset && series.values
          ? series.values.map((value) => ({
              ...value,
              label: nameForAsset(value.label),
            }))
          : series.values,
    };
  });
}

/**
 * The render style family for a spec — every series shares one (scatter vs aggregate).
 *
 * @param {ChartSpec} spec - The chart spec.
 * @return {'scatter' | 'line' | 'bar' | 'stackedBar'} The style family.
 */
export const familyOf = (
  spec: ChartSpec
): 'scatter' | 'line' | 'bar' | 'stackedBar' => spec.series[0]?.style ?? 'bar';

/** One legend swatch — its `SERIES_COLORS` palette slot (`colorIndex`) + its label. */
export interface LegendItem {
  colorIndex: number;
  label: string;
}

/**
 * A legend (swatch + label) for a chart — one item per rendered series, labelled by
 * `legendLabel` (the colorBy group value for a fan-out, else the human field label from
 * `labelByField`, falling back to the raw series name), so EVERY chart legends the same way as
 * Production Volumes. Covers stacked segments, multi-metric overlays, colorBy fan-out, AND
 * single-series charts (which get a one-item legend, e.g. Cumulative Cash Flow); only a chart
 * with NO series (loading / empty) gets `null`. Each item's `colorIndex` is its palette slot,
 * index-aligned with the series `renderChartFamily` draws.
 *
 * @param {ChartSeriesEntry[]} seriesData - The response series being rendered.
 * @param {Map<string, string>} [labelByField] - Field key → display label, from `chart_field_options`.
 * @return {LegendItem[] | null} The legend items, or null when there are no series.
 */
export function buildLegend(
  seriesData: ChartSeriesEntry[],
  labelByField?: Map<string, string>
): LegendItem[] | null {
  if (seriesData.length === 0) {
    return null;
  }
  return seriesData.map((series, i) => ({
    colorIndex: i % SERIES_COLORS.length,
    label: displayLabel(legendLabel(series, labelByField)),
  }));
}

/**
 * Whether every non-empty label parses as a `YYYY-MM` month (a temporal X-axis).
 *
 * @param {ChartSeriesEntry[]} seriesData - The response series being drawn.
 * @return {boolean} True when every label parses as a `YYYY-MM` month.
 */
export function labelsAreMonths(seriesData: ChartSeriesEntry[]): boolean {
  const labels = seriesData.flatMap((s) =>
    (s.values ?? []).map((v) => v.label)
  );
  return (
    labels.length > 0 &&
    labels.every((label) => moment(label, 'YYYY-MM', true).isValid())
  );
}

/**
 * The sorted union of category labels across all series (the shared bar/line X-axis).
 *
 * @param {ChartSeriesEntry[]} seriesData - The response series being drawn.
 * @return {string[]} The sorted, de-duplicated category labels.
 */
export function categoryLabels(seriesData: ChartSeriesEntry[]): string[] {
  return Array.from(
    seriesData.reduce((set, series) => {
      toBarValues(series).forEach((v) => set.add(v.label));
      return set;
    }, new Set<string>())
  ).sort();
}

/**
 * Optional axis chrome the caller supplies. The catalog renderer leaves these unset (its
 * axes are pre-styled); the Custom builder passes field-derived titles + a `format`-aware
 * number formatter. `xFormat` is used only for a scatter (numeric) X.
 */
export interface RenderAxisConfig {
  xTitle?: string;
  yTitle?: string;
  xFormat?: (value: number) => string;
  yFormat?: (value: number) => string;
}

/**
 * Draws a `chart_series` result onto a SciChart surface. Clears the surface first, resets
 * the X-axis fully for the family (so switching chart type in the Custom builder is clean),
 * then adds one renderable per series. Handles all four families:
 * - `scatter` — one scatter renderable per series (numeric X/Y).
 * - `line` — a time line when X is `YYYY-MM` months, else a categorical index line.
 * - `bar` — grouped columns over the union of category labels.
 * - `stackedBar` — one `StackedColumnRenderableSeries` per series, summed per category.
 *
 * @param {SciChartInitContext} ctx - The surface + wasm context (from `SciChartView`).
 * @param {object} opts - `family`, the `seriesData` to draw, and optional `axis` chrome.
 * @return {void}
 */
export function renderChartFamily(
  { surface, wasmContext }: SciChartInitContext,
  {
    family,
    seriesData,
    axis,
  }: {
    family: 'scatter' | 'line' | 'bar' | 'stackedBar';
    seriesData: ChartSeriesEntry[];
    axis?: RenderAxisConfig;
  }
): void {
  const xAxis = surface.xAxes.get(0);
  const yAxis = surface.yAxes.get(0);

  if (axis?.xTitle !== undefined) {
    xAxis.axisTitle = axis.xTitle;
  }
  if (axis?.yTitle !== undefined) {
    yAxis.axisTitle = axis.yTitle;
  }
  if (axis?.yFormat) {
    (yAxis.labelProvider as NumericLabelProvider).formatLabel = axis.yFormat;
  }

  surface.renderableSeries.clear();

  // A continuous (numeric / temporal) X-axis — used by scatter and the temporal line.
  const applyContinuousXAxis = (formatLabel: (value: number) => string) => {
    xAxis.drawMajorGridLines = false;
    xAxis.autoRange = EAutoRange.Always;
    xAxis.autoTicks = true;
    // Reset any category IndexTickProvider a PRIOR render installed — its stale `count`
    // (0 → getMajorTicks returns []) would leave this continuous axis with no ticks, so
    // no labels. This happens when the surface paints empty first (the category branch,
    // since labelsAreMonths([]) is false) and then re-renders once chart_series resolves
    // as months (this branch) — the "years vanish on refresh" bug on the temporal line
    // charts (Production Volumes / Cumulative Cash Flow), and equally when switching
    // family from bar to scatter/line in the Custom builder.
    xAxis.tickProvider = new NumericTickProvider(wasmContext);
    (xAxis.labelProvider as NumericLabelProvider).formatLabel = formatLabel;
  };

  // A category (index) X-axis — one tick per label — used by bar/stackedBar and the
  // categorical line. Closes over `xAxis`/`wasmContext` so callers don't thread the axis type.
  const applyCategoryXAxis = (labels: string[]) => {
    xAxis.autoRange = EAutoRange.Never;
    xAxis.autoTicks = false;
    xAxis.drawMajorGridLines = true;
    const tickProvider = new IndexTickProvider(wasmContext);
    tickProvider.count = labels.length;
    xAxis.tickProvider = tickProvider;
    (xAxis.labelProvider as NumericLabelProvider).formatLabel = (v) =>
      displayLabel(labels[Math.round(v)]);
    xAxis.visibleRange = new NumberRange(
      -0.7,
      Math.max(labels.length, 1) - 0.3
    );
  };

  if (family === 'scatter') {
    applyContinuousXAxis(axis?.xFormat ?? compact);
    seriesData.forEach((series, i) => {
      const points = toScatterPoints(series);
      surface.renderableSeries.add(
        new XyScatterRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: points.map((p) => p.x),
            yValues: points.map((p) => p.y),
          }),
          pointMarker: new EllipsePointMarker(wasmContext, {
            width: 7,
            height: 7,
            fill: SERIES_COLORS[i % SERIES_COLORS.length],
            strokeThickness: 0,
          }),
          opacity: 0.85,
          xAxisId: 'xAxis',
          yAxisId: 'yAxis',
        })
      );
    });
    return;
  }

  if (family === 'line') {
    // Temporal X (`YYYY-MM`) → an epoch-ms time line; categorical X → an index line whose
    // ticks are the category labels (this is what makes a category-X line render at all).
    if (labelsAreMonths(seriesData)) {
      applyContinuousXAxis((v) => moment.utc(v).format('YYYY-MM'));
      seriesData.forEach((series, i) => {
        const points = toLineSeries(series);
        if (points.length === 0) {
          return;
        }
        surface.renderableSeries.add(
          new FastLineRenderableSeries(wasmContext, {
            dataSeries: new XyDataSeries(wasmContext, {
              xValues: points.map(([x]) => x),
              yValues: points.map(([, y]) => y),
            }),
            stroke: SERIES_COLORS[i % SERIES_COLORS.length],
            strokeThickness: 2,
            xAxisId: 'xAxis',
            yAxisId: 'yAxis',
          })
        );
      });
      return;
    }

    const labels = categoryLabels(seriesData);
    applyCategoryXAxis(labels);
    seriesData.forEach((series, i) => {
      const byLabel = new Map(
        toBarValues(series).map((v) => [v.label, v.value])
      );
      surface.renderableSeries.add(
        new FastLineRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: labels.map((_, idx) => idx),
            yValues: labels.map((label) => byLabel.get(label) ?? 0),
          }),
          stroke: SERIES_COLORS[i % SERIES_COLORS.length],
          strokeThickness: 2,
          xAxisId: 'xAxis',
          yAxisId: 'yAxis',
        })
      );
    });
    return;
  }

  // bar / stackedBar: a shared category (index) X-axis over the union of labels.
  const labels = categoryLabels(seriesData);
  applyCategoryXAxis(labels);

  const columnFor = (series: ChartSeriesEntry) => {
    const byLabel = new Map(toBarValues(series).map((v) => [v.label, v.value]));
    return new XyDataSeries(wasmContext, {
      xValues: labels.map((_, i) => i),
      yValues: labels.map((label) => byLabel.get(label) ?? 0),
      dataSeriesName: series.name,
    });
  };

  if (family === 'stackedBar') {
    // Real stacking: one StackedColumnRenderableSeries per series (per colorBy value), all in
    // one collection so the segments sum per category.
    const collection = new StackedColumnCollection(wasmContext, {
      xAxisId: 'xAxis',
      yAxisId: 'yAxis',
    });
    seriesData.forEach((series, i) => {
      collection.add(
        new StackedColumnRenderableSeries(wasmContext, {
          dataSeries: columnFor(series),
          fill: SERIES_COLORS[i % SERIES_COLORS.length],
          stroke: 'transparent',
          strokeThickness: 0,
          stackedGroupId: 'stack',
        })
      );
    });
    surface.renderableSeries.add(collection);
    return;
  }

  // plain bar: one column set per series (grouped).
  seriesData.forEach((series, i) => {
    surface.renderableSeries.add(
      new FastColumnRenderableSeries(wasmContext, {
        dataSeries: columnFor(series),
        fill: SERIES_COLORS[i % SERIES_COLORS.length],
        stroke: 'transparent',
        strokeThickness: 0,
        dataPointWidth: 0.7,
        xAxisId: 'xAxis',
        yAxisId: 'yAxis',
      })
    );
  });
}
